import type { MetaDescriptor } from 'react-router';
import removeMarkdown from 'remove-markdown';

type SeoInput = {
  /** Storefront FQDN — used to build absolute canonical / hreflang URLs. */
  host: string;
  /** Every language the storefront supports (raw backoffice codes). */
  languages: string[];
  /** Storefront's default language (no path prefix). */
  defaultLanguage: string;
  /**
   * The locale prefix to use for the canonical URL — the profile-configured
   * form for the *resolved* locale, not the verbatim URL segment. `undefined`
   * for the default language (no prefix). Because `/en` and `/en-us` both
   * resolve to the same content, they share one canonical (`/en`), so we
   * dedupe by deriving this from the resolved locale rather than the URL.
   */
  canonicalLocale: string | undefined;
  /**
   * Returns the path for a given locale. For the default locale the SEO
   * module passes `undefined`, which the caller is expected to forward to
   * react-router's `href` so the optional `:locale?` segment is omitted.
   * Typical implementation:
   *
   *   buildPath: (locale) => href('/:locale?/experience/:experienceId', { locale, experienceId })
   */
  buildPath: (locale: string | undefined) => string;
  /** Optional query parameters. Stabilised before being baked into the URL. */
  query?: Record<string, string | undefined>;
  title?: string | null;
  /** Plain-text or markdown description. Markdown / HTML is stripped automatically. */
  description?: string | null;
  /** Open Graph image URL. Validated as `https:` before being emitted. */
  ogImage?: string | null;
  /** Open Graph type. Defaults to `'website'`. */
  ogType?: string;
  noindex?: boolean;
  nofollow?: boolean;
};

const MAX_DESCRIPTION_LENGTH = 200;
// Hostname-with-optional-port. Conservative: lowercase letters, digits,
// dots, hyphens. Tenant `fqdn` from storefront-core is trusted, but on
// Vercel preview deployments the storefront resolves `?host=` from the
// query — so this is defence-in-depth against canonical/og:url poisoning.
const HOST_PATTERN = /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?(?::\d+)?$/i;

export function buildSeo(input: SeoInput): MetaDescriptor[] {
  const tags: MetaDescriptor[] = [];
  const description = sanitiseDescription(input.description);
  const ogImage = sanitiseImageUrl(input.ogImage);

  if (input.title) {
    tags.push({ title: input.title });
    tags.push({ property: 'og:title', content: input.title });
    tags.push({ name: 'twitter:title', content: input.title });
  }

  if (description) {
    tags.push({ name: 'description', content: description });
    tags.push({ property: 'og:description', content: description });
    tags.push({ name: 'twitter:description', content: description });
  }

  // URL-bearing tags only render when host is well-formed. A malformed
  // `host` earlier in the pipeline would otherwise produce a misleading
  // canonical pointing at attacker-controlled origin.
  const validHost = HOST_PATTERN.test(input.host) ? input.host : null;

  if (validHost) {
    const stableQuery = stabiliseQuery(input.query);

    // Canonical points to the configured form of the resolved locale (not the
    // verbatim URL segment), so `/en` and `/en-us` — which serve identical
    // content — advertise a single canonical and don't compete as duplicates.
    const canonical = absoluteUrl(
      validHost,
      input.buildPath(input.canonicalLocale),
      stableQuery
    );
    tags.push({ tagName: 'link', rel: 'canonical', href: canonical });
    tags.push({ property: 'og:url', content: canonical });

    // Default-language path is the same href shared by both the
    // default-language alternate and `x-default`. Compute it once.
    const defaultLangHref = absoluteUrl(
      validHost,
      input.buildPath(undefined),
      stableQuery
    );

    const seenLanguages = new Set<string>();
    for (const language of input.languages) {
      if (!language || seenLanguages.has(language)) continue;
      seenLanguages.add(language);
      tags.push({
        tagName: 'link',
        rel: 'alternate',
        hrefLang: language,
        href:
          language === input.defaultLanguage
            ? defaultLangHref
            : absoluteUrl(
                validHost,
                input.buildPath(language),
                stableQuery
              ),
      });
    }

    // x-default — used by search engines as a fallback when none of the
    // available hreflangs match the user's locale.
    tags.push({
      tagName: 'link',
      rel: 'alternate',
      hrefLang: 'x-default',
      href: defaultLangHref,
    });
  }

  tags.push({ property: 'og:type', content: input.ogType ?? 'website' });
  tags.push({
    name: 'twitter:card',
    content: ogImage ? 'summary_large_image' : 'summary',
  });

  if (ogImage) {
    tags.push({ property: 'og:image', content: ogImage });
    tags.push({ name: 'twitter:image', content: ogImage });
  }

  // Only emit the directives that are *restricting* — `index` and `follow`
  // are the implicit defaults for crawlers and including them in the tag
  // is non-standard noise.
  const directives: string[] = [];
  if (input.noindex) directives.push('noindex');
  if (input.nofollow) directives.push('nofollow');
  if (directives.length > 0) {
    tags.push({ name: 'robots', content: directives.join(', ') });
  }

  return tags;
}

function sanitiseDescription(value: string | null | undefined): string | null {
  if (!value) return null;
  // Strip markdown then any residual HTML tags — `remove-markdown` strips
  // markdown syntax but leaves raw `<script>…` / `<img onerror=…>` text in
  // place. React DOM escapes attribute values, but the description also
  // shows up in non-HTML consumers (sitemaps, JSON-LD, social previews),
  // so we sanitise here.
  const cleaned = removeMarkdown(value)
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return null;
  return truncateGraphemes(cleaned, MAX_DESCRIPTION_LENGTH);
}

function sanitiseImageUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  // Refuse non-https. `javascript:` / `data:` are obvious XSS-via-preview
  // risks; `http:` is mixed-content and most crawlers ignore it.
  if (url.protocol !== 'https:') return null;
  return url.toString();
}

function absoluteUrl(host: string, path: string, query: string): string {
  return `https://${host}${path}${query}`;
}

function stabiliseQuery(
  query: Record<string, string | undefined> | undefined
): string {
  if (!query) return '';
  const entries = Object.entries(query)
    .filter((entry): entry is [string, string] => entry[1] !== undefined)
    .sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return '';

  const params = new URLSearchParams();
  for (const [key, value] of entries) {
    const deduped = Array.from(new Set(value.split(','))).sort((a, b) =>
      a.localeCompare(b)
    );
    params.set(key, deduped.join(','));
  }
  const asString = params.toString();
  return asString ? `?${asString}` : '';
}

/**
 * Slice a string to a maximum number of code points without splitting a
 * surrogate pair. Adds an ellipsis when truncation happens.
 *
 * Note: this is grapheme-safe at the code-point level, not at the
 * extended-grapheme-cluster level. A flag emoji (RGI sequence) or a
 * skin-tone-modified emoji would be cut between code points. `Intl.Segmenter`
 * with `granularity: 'grapheme'` would handle those, but isn't available in
 * the JSDOM test environment we run vitest in. Acceptable trade-off for a
 * meta description that gets truncated below 200 chars anyway — search
 * engines render at most ~155.
 */
function truncateGraphemes(text: string, max: number): string {
  const codePoints = Array.from(text);
  if (codePoints.length <= max) return text;
  return `${codePoints.slice(0, max - 1).join('').trimEnd()}…`;
}
